/**
 * @module app.shared
 */
import { TaskQueue, observable, autoinject, processContent, useView, PLATFORM, computedFrom } from 'aurelia-framework';
import { InputBase } from 'app/shared/elements/forms/input-base';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { isNullOrUndefined } from 'app/shared/lib/utilities';
import { moveRulesToHelperSlot } from 'app/shared/elements/ess-rule';
import { updateMaterializeInputLabel } from 'app/shared/lib/materialize/dom-utilities';
import { I18N } from 'aurelia-i18n';

/**
 * Remove Non Numeric characters from the input except for a single period as a decimal seperator
 */
function stripNonNumeric(value: string): string {
    return value.replace(/[^\d\.-]|(?<=\..*)\.|(?<!^\d*)-/g, ''); // |^0+(?!\.|$)/g, ''); don't strip leading zeroes for now
}

/**
 * Slice off decimal digits beyond a precision value
 * @param value A decimal number
 * @param precision The number of precision digits to preserve
 */
function forcePrecision(value: string, precision: number): string {
    const [integer, decimal] = value.split('.');
    if(precision === 0) {
        return integer;
    }
    if(decimal?.length > 0) {
        return `${integer}.${decimal.slice(0, precision)}`;
    }
    return value;
}

@autoinject()
@processContent(moveRulesToHelperSlot)
@useView(PLATFORM.moduleName('./ess-number.html'))
export abstract class NumberInputBase extends InputBase {

    /** Underlying number value for the input */
    public value: number | null = null;
    protected min: number | null = Number.MIN_SAFE_INTEGER;
    protected max: number | null = Number.MAX_SAFE_INTEGER;
    protected step: number | null = null;
    protected precision: number | null = null;
    public icon: string | null = null;
    public testPrefix: string | null = null;
    public testName: string | null = null;
    public requiredValidatorLabel: string | null = null;

    protected minLength: number | null = 0;
    // INFO [Matt] Currently, we only support 15 digits of overall precision,
    //  the maximum reliable number of digits in JavaScript numbers. In the
    //  future, we can store numbers as strings for more precision.
    // NOTE [Matt] This includes - and . characters. 16 accounts for a decimal.
    //  If the input value has no decimal, then max value should cover it. If
    //  the input value is negative, then this is too restrictive and we have
    //  a bug.
    protected maxLength: number | null = 16;

    /** The internally tracked `<input>`'s `value` property */
    @observable()
    protected inputValue: string = '';

    // INFO [Matt] For the required rule. Actually, this should be updated on
    //  the $rules definition, but that's a high risk change and inappropriate
    //  at the moment.
    protected isNullOrUndefined = isNullOrUndefined;

    protected $inputElement!: HTMLInputElement;
    protected $labelElement!: HTMLLabelElement;

    constructor(element: Element, logger: AppLogger, protected queue: TaskQueue, private readonly i18n: I18N) {
        super(element, logger);
    }

    attached() {
        if(!this.testPrefix) {
            if(this.precision && this.precision > 0) {
                this.testPrefix = 'dec';
            }
            else {
                this.testPrefix = 'num';
            }
        }
    }

    protected valueChanged(value: string | number | null | unknown) {
        if(typeof value === 'string') {
            this.queue.queueMicroTask(() => this.inputValue = stripNonNumeric(value));
        }
        else if(isNullOrUndefined(value)) {
            this.inputValue = '';
            this.queue.queueMicroTask(() => this.value = null);
        }
        else if(this.inputValue === '' || this.value !== Number(this.inputValue)) {
            this.inputValue = stripNonNumeric(String(value));
        }
    }

    protected inputValueChanged(input: string) {
        const num = Number(input);
        if(input === '') {
            this.value = null;
        }
        else if(!isNaN(num) && num !== this.value) {
            this.value = num;
        }

        if(this.$inputElement === document.activeElement) {
            this.queue.queueTask(() => {
                updateMaterializeInputLabel(this.$inputElement, this.$labelElement);
            });
        }
    }

    @computedFrom('label', 'requiredValidatorLabel')
    public get requiredValueMessage() {
        return this.requiredValidatorLabel || this.i18n.tr('ui:validation:required', { what: this.i18n.tr(this.label || 'ui:labels.value') });
    }

    @computedFrom('value')
    protected get length(): number {
        return this.value?.toString()?.length || 0;
    }

    // INFO [Matt] Aurelia pulls the updated value from $inputElement.value
    //  and updates bindings on the input event. By modifying $inputElement.value
    //  in the capture phase we are able to process the value before Aurelia gets
    //  to it, causing Aurelia to use our updated value instead.
    protected guardInput() {
        let { value } = this.$inputElement;
        value = stripNonNumeric(value);
        if(this.precision !== null) {
            value = forcePrecision(value, this.precision);
        }
        this.$inputElement.value = value;
    }

    protected reconcileInput() {
        if(isNaN(Number(this.inputValue))) {
            this.inputValue = '';
        }
    }

    /**
     * Coerce a string value to number in situations where we aren't sure if we're getting a number or a string
     */
    protected coerceNumber(value: string | number | null | undefined): number | null {
        if(typeof value === 'number') { return value; }
        else if(isNullOrUndefined(value)) { return null; }

        const num = Number(stripNonNumeric(value));
        if(Number.isNaN(num)) { return null; }
        return num;
    }

}
